import React, { useEffect, useRef } from 'react';
import { addDays } from '../utils/travelState';

interface DateRangePickerProps {
  label: string;
  startDate?: string;
  endDate?: string;
  onChange: (start: string, end: string) => void;
  className?: string;
  startLabel?: string;
  endLabel?: string;
  /** Stack start/end as two full-width single-line fields instead of a 2-col grid. */
  stacked?: boolean;
  /** Hide the outer group label — useful when startLabel/endLabel already say enough. */
  hideGroupLabel?: boolean;
}

// A native <input type="date"> fires onChange for every keystroke across its
// day/month/year segments, not just on a complete date — mid-typing, e.target.value
// is "" until all three segments are filled in. Propagating that straight through
// used to blank the field on every keystroke. isCompleteOrEmpty() lets a genuine
// clear (backspacing everything) through, but ignores the transient partial states.
const isCompleteOrEmpty = (v: string) => v === '' || v.length === 10;

// The year segment rolls through intermediate "complete-looking" states while being
// typed (typing "2027" passes through 0002 → 0020 → 0202 → 2027 as each digit lands,
// each one already 10 chars). Committing those straight through was doubly harmful:
// (1) feeding them back into this input's `value` prop makes the browser treat it as
// an external reset and drop its own in-progress segment buffer, and (2) upstream,
// this app re-sorts destination lists by arrival date on every commit — an
// in-progress year like "0002" reorders the array, which changes this field's React
// key and remounts it. Either alone truncates what's mid-typing (e.g. "2027" landing
// as "0027"). Fix: keep the field uncontrolled while focused, and debounce commits so
// only the value after a pause (or on blur) ever reaches shared state.
const COMMIT_DEBOUNCE_MS = 500;

function DateField({
  value,
  onCommit,
  ...inputProps
}: {
  value: string;
  onCommit: (v: string) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'defaultValue'>) {
  const ref = useRef<HTMLInputElement>(null);
  const pending = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el && el.value !== value) {
      el.value = value;
    }
  }, [value]);

  const flush = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (pending.current !== null) {
      const v = pending.current;
      pending.current = null;
      if (isCompleteOrEmpty(v)) onCommit(v);
    }
  };

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  return (
    <input
      ref={ref}
      type="date"
      defaultValue={value}
      onChange={(e) => {
        pending.current = e.target.value;
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(flush, COMMIT_DEBOUNCE_MS);
      }}
      onBlur={flush}
      {...inputProps}
    />
  );
}

export function DateRangePicker({ label, startDate, endDate, onChange, className, startLabel = 'From', endLabel = 'To', stacked = false, hideGroupLabel = false }: DateRangePickerProps) {
  const handleStartChange = (newStart: string) => {
    // No end date yet, or it's no longer after the new start — default it to
    // the day after arrival (not the same day) so departure isn't left empty
    // and its calendar opens already showing the right month.
    const newEnd = !newStart
      ? endDate ?? ''
      : (!endDate || endDate <= newStart) ? addDays(newStart, 1) : endDate;
    onChange(newStart, newEnd);
  };

  const handleEndChange = (newEnd: string) => {
    // If start is after the new end, pull start back to match
    const newStart = startDate && newEnd && startDate > newEnd ? newEnd : (startDate ?? newEnd);
    onChange(newStart, newEnd);
  };

  return (
    <div className={`space-y-1.5 ${className}`}>
      {!hideGroupLabel && (
        <label className="text-[11px] font-black uppercase text-app-sub opacity-50 tracking-widest block pl-1">
          {label}
        </label>
      )}
      <div className={stacked ? 'space-y-4' : 'grid grid-cols-2 gap-3'}>
        <div className="space-y-1">
          <label className="text-[11px] font-black uppercase text-app-sub opacity-40 tracking-widest block pl-1">
            {startLabel}
          </label>
          <DateField
            value={startDate ?? ''}
            onCommit={handleStartChange}
            name="date-from"
            autoComplete="off"
            max={endDate ?? undefined}
            className="w-full bg-white border border-app-border/60 rounded-xl px-3 py-3 text-sm text-app-text font-normal focus:outline-none focus:ring-4 focus:ring-gold/5 focus:border-gold/40 transition appearance-none"
            style={{ colorScheme: 'light' }}
          />
        </div>
        <div className="space-y-1">
          <label className="text-[11px] font-black uppercase text-app-sub opacity-40 tracking-widest block pl-1">
            {endLabel}
          </label>
          <DateField
            value={endDate ?? ''}
            onCommit={handleEndChange}
            name="date-to"
            autoComplete="off"
            min={startDate ?? undefined}
            className="w-full bg-white border border-app-border/60 rounded-xl px-3 py-3 text-sm text-app-text font-normal focus:outline-none focus:ring-4 focus:ring-gold/5 focus:border-gold/40 transition appearance-none"
            style={{ colorScheme: 'light' }}
          />
        </div>
      </div>
    </div>
  );
}
